import {
  getVaccinationNextItemId,
  pickPreferredVaccineAppointment,
  type AppointmentLike,
  type VaccinationItemIdInput,
} from "@/lib/applications/pending-utils";

const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ACTIVE_APPOINTMENT_STATUSES = new Set(["agendado", "confirmado", "em_atendimento"]);

type FutureComparable = {
  id: string;
  pet_id?: string | null;
  next_dose_date?: string | null;
  has_next_dose?: boolean | null;
  next_dose_completed_at?: string | null;
  appointment?: { id?: string | null } | null;
} & VaccinationItemIdInput;

export type ClassifiedVaccinationUpcomingRow = {
  vaccinationId: string;
  petId: string | null;
  itemId: string | null;
  vaccineName: string;
  nextDoseRaw: string;
  nextDoseTimestamp: number;
  isDateOnly: boolean;
  resolutionStatus: "scheduled" | "pending";
  appointment?: AppointmentLike | null;
};

type ClassifyUpcomingVaccinationRowsInput<T extends FutureComparable> = {
  vaccinations: T[];
  appointments: AppointmentLike[];
  now?: Date;
  includePast?: boolean;
  getVaccineName: (vaccination: T) => string;
};

const parseNextDose = (
  value: string,
  now: Date,
  includePast: boolean,
): { timestamp: number; isDateOnly: boolean } | null => {
  if (ISO_DATE_ONLY_REGEX.test(value)) {
    const todayIso = now.toISOString().slice(0, 10);
    if (!includePast && value < todayIso) return null;
    const safe = new Date(`${value}T12:00:00.000Z`);
    if (Number.isNaN(safe.getTime())) return null;
    return { timestamp: safe.getTime(), isDateOnly: true };
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  if (!includePast && parsed.getTime() <= now.getTime()) return null;
  return { timestamp: parsed.getTime(), isDateOnly: false };
};

export const classifyUpcomingVaccinationRows = <T extends FutureComparable>({
  vaccinations,
  appointments,
  now = new Date(),
  includePast = false,
  getVaccineName,
}: ClassifyUpcomingVaccinationRowsInput<T>): ClassifiedVaccinationUpcomingRow[] => {
  if (!vaccinations.length) return [];

  return vaccinations
    .map((vacc) => {
      if (vacc.has_next_dose === false) return null;
      if (vacc.next_dose_completed_at) return null;

      const nextDose = typeof vacc.next_dose_date === "string"
        ? parseNextDose(vacc.next_dose_date, now, includePast)
        : null;
      if (!nextDose) return null;

      const petId = vacc.pet_id ?? null;
      const itemId = getVaccinationNextItemId(vacc);

      const preferredAppt = pickPreferredVaccineAppointment({
        appointment_id: vacc.appointment?.id ?? null,
        next_dose_date: vacc.next_dose_date ?? null,
        appointments,
      });

      const isScheduled =
        !!preferredAppt?.id &&
        ACTIVE_APPOINTMENT_STATUSES.has(preferredAppt.status ?? "");

      return {
        vaccinationId: vacc.id,
        petId,
        itemId,
        vaccineName: getVaccineName(vacc),
        nextDoseRaw: vacc.next_dose_date ?? "",
        nextDoseTimestamp: nextDose.timestamp,
        isDateOnly: nextDose.isDateOnly,
        resolutionStatus: isScheduled ? "scheduled" : "pending",
        appointment: isScheduled ? preferredAppt : null,
      } satisfies ClassifiedVaccinationUpcomingRow;
    })
    .filter(Boolean)
    .sort((a, b) => a.nextDoseTimestamp - b.nextDoseTimestamp) as ClassifiedVaccinationUpcomingRow[];
};
